import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { EASE } from "./Shared";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const launch = () => {
    scrollToId("#pricing");
    window.dispatchEvent(new CustomEvent("grapho:highlight-trial"));
  };

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pb-12 pt-24 text-center sm:pb-16 sm:pt-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% -20%, rgba(56,189,248,0.16), transparent 65%), radial-gradient(circle at 82% 62%, rgba(245,158,11,0.09), transparent 55%)",
        }}
      />

      <motion.div style={{ y, opacity }} className="relative flex w-full max-w-5xl flex-col items-center">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05, ease: EASE }}
          className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-400 sm:text-xs"
        >
          Original Designs for Etsy Sellers
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="mt-7 text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-[4.6rem]"
        >
          <MaskedLine delay={0.18}>Stop Designing.</MaskedLine>
          <MaskedLine delay={0.34}>
            <span className="text-gradient-gold">Start Listing.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
          className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:mt-7 sm:text-lg"
        >
          Your private design team for Etsy. Send us your niche, idea, and style. We create original
          production-ready designs for your shop — fast, consistently, and exclusively.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.78, ease: EASE }}
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
        >
          <button
            data-testid="hero-trial-cta-button"
            onClick={launch}
            className="group inline-flex items-center gap-2.5 rounded-full bg-amber-400 px-8 py-4 text-base font-bold text-[#1A1204] transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-300 hover:shadow-[0_0_40px_rgba(245,158,11,0.5)]"
          >
            Get My First Design for $1
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-pricing-cta-button"
            onClick={() => scrollToId("#pricing")}
            className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur transition-all duration-300 hover:border-sky-400/50 hover:bg-white/10"
          >
            View Pricing
            <ArrowDown className="h-4 w-4" />
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-trust-pills"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.92, ease: EASE }}
          className="mt-9 flex flex-wrap items-center justify-center gap-2"
        >
          {["Original Designs", "300 DPI PNG", "Production Ready", "Full Commercial License", "Fast Turnaround"].map(
            (pill) => (
              <span
                key={pill}
                data-testid="hero-trust-pill"
                className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-[11px] font-semibold text-slate-300"
              >
                {pill}
              </span>
            )
          )}
        </motion.div>

      </motion.div>
    </section>
  );
}
